import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { ActionIcon, Badge, Menu, Text, Tooltip, Flex, Collapse, px } from '@mantine/core';
import { useLayoutEffect, useRef, useState } from 'react';
import {
	isLeafItem,
	isParentItem,
	QuickSwitchConfigItem,
} from '@dealstack/frontend/shared/components/QuickSwitch/QuickSwitchMenu.types';
import {
	QuickSwitchMenuDropdown,
	QuickSwitchMenuItem,
	QuickSwitchTriggerGroup,
} from '@dealstack/frontend/shared/components/QuickSwitch/QuickSwitchMenu.styled';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { GENERIC_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds.ts';

interface Props {
	menuItems: QuickSwitchConfigItem[];
	dealRoute: string;
}

const QuickSwitchConditionalTooltip = ({ item }: { item: QuickSwitchConfigItem }) => {
	const textRef = useRef<HTMLDivElement>(null);
	const [showTooltip, setShowTooltip] = useState(false);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	const tCommonUi = useTranslationCommonUi();
	const [isOpen, setIsOpen] = useState(false);

	const location = useLocation();
	const navigate = useNavigate();

	const isItemActive =
		isLeafItem(item) &&
		(item.matchOnlyExactRoutes
			? location.pathname === item.route
			: location.pathname.includes(item.route));
	const isSubItemActive = (subRoute: string) => location.pathname === subRoute;

	const isParentActive =
		isParentItem(item) && item.items.some((subItem) => location.pathname === subItem.route);

	useLayoutEffect(() => {
		if (!textRef.current) {
			return;
		}

		const checkOverflow = () => {
			if (textRef.current) {
				const hasOverflow = textRef.current.scrollHeight > textRef.current.clientHeight;
				setShowTooltip(hasOverflow);
			}
		};

		checkOverflow();

		resizeObserverRef.current = new ResizeObserver(() => {
			checkOverflow();
		});
		resizeObserverRef.current.observe(textRef.current);

		return () => {
			if (resizeObserverRef.current) {
				resizeObserverRef.current.disconnect();
			}
		};
	}, [item.label]);

	const hasSubItems = isParentItem(item);

	return (
		<>
			<QuickSwitchMenuItem
				$isActive={isItemActive || isParentActive}
				data-testid={item.dataTestId}
				onClick={() => {
					if (hasSubItems) {
						setIsOpen(!isOpen);
					} else if (isLeafItem(item)) {
						navigate(item.route);
					}
				}}
			>
				<Flex w="15rem" align="center" gap="0.5rem" justify="space-between">
					<Flex flex={1} miw={0} align="center" gap="0.5rem">
						<Icon variant={item.icon} size="lg" color="dark.9" />
						<Tooltip
							label={tCommonUi(item.label)}
							events={{ hover: showTooltip, focus: showTooltip, touch: showTooltip }}
						>
							<Text size="B6Medium" c="dark.9" lineClamp={1} truncate ref={textRef}>
								{tCommonUi(item.label)}
							</Text>
						</Tooltip>
					</Flex>

					{item.badge && !isItemActive && (
						<Badge
							size="sm"
							variant="filled"
							style={{ flexShrink: 0 }}
							data-testid={GENERIC_ELEMENT_ID.QUICK_SWITCHER_MENU_ITEM_BADGE}
						>
							{item.badge}
						</Badge>
					)}

					{hasSubItems && (
						<Icon
							variant="caret-right"
							size="lg"
							color="dark.9"
							onClick={(e) => {
								e.stopPropagation();
								setIsOpen(!isOpen);
							}}
							style={{
								transform: isOpen ? 'rotate(90deg)' : 'none',
								transition: 'transform 0.2s',
							}}
						/>
					)}
				</Flex>
			</QuickSwitchMenuItem>
			{hasSubItems && isParentItem(item) && (
				<Collapse in={isOpen}>
					<Flex direction="column" gap="0.5rem">
						{item.items.map((subItem) => (
							<QuickSwitchMenuItem
								$isSubItem
								$isActive={isSubItemActive(subItem.route)}
								onClick={() => navigate(subItem.route)}
								key={subItem.id}
							>
								<Text
									size="B6Medium"
									c="dark.9"
									lineClamp={1}
									truncate
									ref={textRef}
								>
									{subItem.label}
								</Text>
							</QuickSwitchMenuItem>
						))}
					</Flex>
				</Collapse>
			)}
		</>
	);
};

export function QuickSwitchMenu({ menuItems, dealRoute }: Props) {
	const [isOpen, setIsOpen] = useState(false);
	const navigate = useNavigate();

	return (
		<Menu
			position="bottom-start"
			offset={Number(px('1rem'))}
			opened={isOpen}
			onClose={() => setIsOpen(false)}
		>
			<QuickSwitchTriggerGroup>
				<ActionIcon size="lg" variant="default" onClick={() => navigate(dealRoute)}>
					<Icon variant="home-v3" size="lg" color="black" />
				</ActionIcon>
				<Menu.Target>
					<ActionIcon
						variant="default"
						data-testid={GENERIC_ELEMENT_ID.QUICK_SWITCHER_TARGET}
						onClick={() => setIsOpen(true)}
						size="lg"
						miw="unset"
						w="24px"
					>
						<Icon variant="caret-down" size="md" color="black" />
					</ActionIcon>
				</Menu.Target>
			</QuickSwitchTriggerGroup>
			<QuickSwitchMenuDropdown>
				<Flex direction="column" gap="0.5rem" py="0" px="0.5rem">
					{menuItems.map((item) => (
						<QuickSwitchConditionalTooltip key={item.id} item={item} />
					))}
				</Flex>
			</QuickSwitchMenuDropdown>
		</Menu>
	);
}
